import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import TutorIndividualLog from './TutorIndividualLog';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiDownload, FiUser, FiCalendar, FiClock, FiEye } = FiIcons;

const TutorSessionsReport = () => {
    const { sessions, students, months } = useData();
    const [selectedMonth, setSelectedMonth] = useState(new Date().toLocaleString('default', { month: 'long' }));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTutor, setSelectedTutor] = useState(null);

    const reportData = useMemo(() => {
        const rows = [];
        Object.keys(sessions).forEach(studentId => {
            const student = students.find(s => s.id === studentId);
            const monthSessions = sessions[studentId][selectedMonth] || [];
            if (monthSessions.length === 0) return;

            const tutorGroups = {};
            monthSessions.forEach(session => {
                const tutor = session.tutor || 'Unassigned';
                const subject = session.subject || 'General';
                const key = `${tutor}-${subject}`;
                
                if (!tutorGroups[key]) {
                    tutorGroups[key] = {
                        tutor,
                        studentName: student?.studentName || 'Unknown Student',
                        grade: student?.grade || '-',
                        subject,
                        count: 0,
                        duration: 0
                    };
                }
                tutorGroups[key].count += 1;
                tutorGroups[key].duration += (parseInt(session.duration) || 0);
            });

            Object.values(tutorGroups).forEach(group => {
                rows.push(group);
            });
        });
        return rows.sort((a, b) => a.tutor.localeCompare(b.tutor));
    }, [sessions, students, selectedMonth]);

    const filteredData = reportData.filter(row => 
        row.tutor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.subject.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const exportCSV = () => {
        const headers = ['Tutor', 'Student', 'Grade', 'Subject', 'Session Count', 'Total Hours'];
        const csvContent = [
            headers.join(','),
            ...filteredData.map(row => 
                `"${row.tutor}","${row.studentName}","${row.grade}","${row.subject}",${row.count},${(row.duration / 60).toFixed(2)}`
            )
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Tutor_Sessions_Report_${selectedMonth}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (selectedTutor) {
      return <TutorIndividualLog tutorName={selectedTutor} onBack={() => setSelectedTutor(null)} />;
    }

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Filters & Actions */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-center bg-white p-5 rounded-[2rem] shadow-sm border border-gray-100">
                <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
                    <div className="relative w-full sm:w-64">
                        <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input 
                            type="text" 
                            placeholder="Search Tutor or Student..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-sm text-gray-700 placeholder-gray-400"
                        />
                    </div>
                    <div className="relative w-full sm:w-48">
                        <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                        <select 
                            value={selectedMonth} 
                            onChange={e => setSelectedMonth(e.target.value)}
                            className="w-full pl-11 pr-8 py-3 bg-gray-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-sm text-gray-700 appearance-none cursor-pointer"
                        >
                            {months.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                </div>
                <button 
                    onClick={exportCSV}
                    disabled={filteredData.length === 0}
                    className="flex items-center gap-2 px-6 py-3 bg-green-50 text-green-700 border-2 border-green-100 rounded-2xl hover:bg-green-100 hover:border-green-200 transition-all font-black text-xs uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto justify-center"
                >
                    <SafeIcon icon={FiDownload} /> Export CSV
                </button>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/50 border-b border-gray-100">
                            <tr>
                                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Tutor</th>
                                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Student</th>
                                <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Subject</th>
                                <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Sessions</th>
                                <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Total Time</th>
                                <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredData.length > 0 ? (
                                filteredData.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50/80 transition-colors group">
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-sm group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                                                    {row.tutor.charAt(0)}
                                                </div>
                                                <span className="font-bold text-gray-900 text-sm">{row.tutor}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                                    <SafeIcon icon={FiUser} className="text-xs" />
                                                </div>
                                                <div>
                                                  <div className="text-sm font-bold text-gray-900">{row.studentName}</div>
                                                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Grade {row.grade}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5">
                                            <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                                                {row.subject}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-center">
                                            <div className="inline-flex flex-col items-center">
                                                <span className="text-lg font-black text-gray-900">{row.count}</span>
                                                <span className="text-[9px] font-bold text-gray-400 uppercase">Classes</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2 text-sm font-bold text-gray-600">
                                                <SafeIcon icon={FiClock} className="text-gray-300" />
                                                {(row.duration / 60).toFixed(1)} hrs
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                          <button 
                                            onClick={() => setSelectedTutor(row.tutor)}
                                            className="p-2.5 bg-gray-50 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all border border-transparent hover:border-indigo-100"
                                          >
                                            <SafeIcon icon={FiEye} className="text-lg" />
                                          </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="px-8 py-16 text-center">
                                        <div className="flex flex-col items-center justify-center">
                                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                                                <SafeIcon icon={FiSearch} className="text-2xl text-gray-300" />
                                            </div>
                                            <p className="text-gray-900 font-bold">No sessions found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TutorSessionsReport;